/**
 * ============================================================================
 * VEDANG JYOTISHAM V6.5 — BTR HISTORICAL TIMEZONE & UTC RESOLVER
 * ============================================================================
 * Specification: VJ-BTR-V65-SPEC-001 v3.0 (Phase 2 Implementation)
 * Provides reliable per-candidate local-time-to-UTC resolution using verified
 * IANA timezone data compiled into the runtime, with explicit handling of
 * historical timezone changes, daylight saving transitions, and ambiguous/
 * nonexistent local times.
 *
 * Strict Rule: Contains astronomical timing and timezone resolution only.
 * Never reuses a static UTC offset across multi-date searches.
 * ============================================================================
 */

import { CandidateTuple } from '../types/btr';
import {
  isValidCalendarDate,
  isValidClockTime,
  isValidIanaTimezone,
  validateCandidateTuple
} from './btrContractValidation';

export const TIMEZONE_ENGINE_VERSION = 'BTR-TZ-V6.5-IANA-CANONICAL';

export type TimezoneResolutionStatus =
  | 'RESOLVED_EXACT'             // Unambiguous, verified 1-to-1 UTC instant
  | 'AMBIGUOUS_FALLBACK_DST'     // Backward DST transition; local time occurs twice
  | 'NONEXISTENT_SPRING_DST'     // Forward DST transition; local clock skipped forward
  | 'UNRESOLVED_TIMEZONE'        // IANA timezone identifier invalid or unsupported
  | 'INVALID_INPUT';             // Malformed date, time, or geographic coordinates

export type DstClassification =
  | 'STANDARD_TIME'
  | 'DAYLIGHT_SAVING_TIME'
  | 'HISTORICAL_WAR_TIME'
  | 'LOCAL_MEAN_TIME';

export interface UtcHypothesis {
  /** Normalized ISO 8601 UTC string ending in 'Z' */
  utcInstant: string;
  /** Epoch milliseconds of UTC instant */
  utcMs: number;
  /** Exact offset in seconds (Local - UTC) */
  utcOffsetSeconds: number;
  /** Formatted offset string (e.g., '+05:30', '-04:00', '+05:21:10') */
  formattedOffset: string;
  /** Historical or seasonal DST status */
  dstStatus: DstClassification;
}

export interface CandidateResolutionResult {
  status: TimezoneResolutionStatus;
  /** Resolved candidate tuple when status is RESOLVED_EXACT */
  resolvedCandidate?: CandidateTuple;
  /** Both valid UTC hypotheses when status is AMBIGUOUS_FALLBACK_DST */
  ambiguousHypotheses?: [UtcHypothesis, UtcHypothesis];
  /** Surrounding boundary timestamps when local time is nonexistent */
  gapBoundaries?: {
    /**
     * Verified UTC transition boundary instant (ISO 8601 UTC).
     * In continuous UTC, the civil clock discontinuity occurs at a singular transition instant
     * where the local clock advances from pre-transition offset to post-transition offset.
     */
    preTransitionUtc: string;
    /**
     * Verified UTC transition boundary instant (identical to preTransitionUtc, guaranteeing pre <= post).
     */
    postTransitionUtc: string;
    /** Exact duration skipped in wall-clock local time in minutes: (offsetAfter - offsetBefore) / 60 */
    gapMinutes: number;
    /** Epoch milliseconds of verified transition boundary instant */
    transitionEpochMs?: number;
  };
  /** Explanatory message or validation error details */
  errorMessage?: string;
  /** True when UTC-to-local round-trip was verified character-for-character */
  roundTripVerified: boolean;
}

export interface RuntimeTimezoneCapabilities {
  nodeVersion: string;
  hasIntl: boolean;
  icuVersion?: string;
  tzVersion?: string;
  isFullySupported: boolean;
}

export interface ResolveCandidateParams {
  localDate: string;          // YYYY-MM-DD
  localClockTime: string;     // HH:mm:ss
  timezoneId: string;         // e.g. 'Asia/Kolkata', 'America/New_York'
  latitude: number;           // -90 to +90
  longitude: number;          // -180 to +180
  placeName?: string;
  sourceProvenance?: string;
}

// ============================================================================
// FORMATTING HELPERS
// ============================================================================

/**
 * Formats an offset in seconds to ISO offset string (+HH:mm, -HH:mm, or +HH:mm:ss).
 */
export function formatOffsetSeconds(offsetSeconds: number): string {
  const sign = offsetSeconds >= 0 ? '+' : '-';
  const totalSec = Math.abs(offsetSeconds);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  const hStr = String(hours).padStart(2, '0');
  const mStr = String(minutes).padStart(2, '0');

  if (seconds > 0) {
    const sStr = String(seconds).padStart(2, '0');
    return `${sign}${hStr}:${mStr}:${sStr}`;
  }
  return `${sign}${hStr}:${mStr}`;
}

/**
 * Extracts decomposed local calendar date and clock time for a given UTC epoch ms.
 */
export function formatUtcToLocal(
  utcMs: number,
  timezoneId: string
): { localDate: string; localTime: string; localAsUtcMs: number } {
  const alignedMs = Math.floor(utcMs / 1000) * 1000;
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: timezoneId,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });

  const parts = dtf.formatToParts(new Date(alignedMs));
  let year = 1970;
  let month = 1;
  let day = 1;
  let hour = 0;
  let minute = 0;
  let second = 0;

  for (const part of parts) {
    if (part.type === 'year') year = parseInt(part.value, 10);
    else if (part.type === 'month') month = parseInt(part.value, 10);
    else if (part.type === 'day') day = parseInt(part.value, 10);
    else if (part.type === 'hour') hour = parseInt(part.value, 10);
    else if (part.type === 'minute') minute = parseInt(part.value, 10);
    else if (part.type === 'second') second = parseInt(part.value, 10);
  }

  const localDate = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const localTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
  const localAsUtcMs = Date.UTC(year, month - 1, day, hour, minute, second);

  return { localDate, localTime, localAsUtcMs };
}

/**
 * Computes exact offset in seconds for a given UTC instant in the target timezone.
 * offsetSeconds = (LocalTimeAsUtc - UtcInstant) / 1000
 */
export function getUtcOffsetSecondsForInstant(
  utcMs: number,
  timezoneId: string
): number {
  const alignedMs = Math.floor(utcMs / 1000) * 1000;
  const { localAsUtcMs } = formatUtcToLocal(alignedMs, timezoneId);
  return Math.round((localAsUtcMs - alignedMs) / 1000);
}

/**
 * Returns runtime environment timezone capabilities and engine assumptions.
 * Guaranteed fully supported on Node.js >= 20.0.0 with full ICU tzdb (tzdb >= 2024a).
 */
export function getRuntimeTimezoneCapabilities(): RuntimeTimezoneCapabilities {
  const hasIntl = typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function';
  const nodeVersion = typeof process !== 'undefined' && process.version ? process.version : 'browser';
  const icuVersion = typeof process !== 'undefined' && process.versions?.icu ? process.versions.icu : undefined;
  const tzVersion = typeof process !== 'undefined' && process.versions?.tz ? process.versions.tz : undefined;

  let isFullySupported = hasIntl;
  if (typeof process !== 'undefined' && process.versions?.node) {
    const major = parseInt(process.versions.node.split('.')[0], 10);
    isFullySupported = hasIntl && major >= 20;
  }

  return { nodeVersion, hasIntl, icuVersion, tzVersion, isFullySupported };
}

/**
 * Binary searches for the exact transition boundary instant in UTC (second precision)
 * where the civil clock jumped forward, creating the nonexistent wall-clock gap.
 */
export function findTransitionBoundary(
  targetLocalAsUtcMs: number,
  timezoneId: string,
  knownOffsets: number[]
): {
  transitionUtcMs: number;
  offsetBefore: number;
  offsetAfter: number;
  gapMinutes: number;
} | null {
  const sortedOffsets = Array.from(new Set(knownOffsets)).sort((a, b) => a - b);
  if (sortedOffsets.length < 2) return null;

  for (let i = 0; i < sortedOffsets.length - 1; i++) {
    const offBefore = sortedOffsets[i];
    const offAfter = sortedOffsets[i + 1];

    const lowBound = targetLocalAsUtcMs - (offAfter * 1000);
    const highBound = targetLocalAsUtcMs - (offBefore * 1000);

    const actualLowOff = getUtcOffsetSecondsForInstant(lowBound, timezoneId);
    const actualHighOff = getUtcOffsetSecondsForInstant(highBound, timezoneId);

    if (actualLowOff <= actualHighOff && actualLowOff === offBefore && actualHighOff === offAfter) {
      let low = Math.floor(lowBound / 1000) * 1000;
      let high = Math.floor(highBound / 1000) * 1000;

      while (high - low > 1000) {
        const mid = Math.floor((low + high) / 2000) * 1000;
        if (mid === low) break;
        const offMid = getUtcOffsetSecondsForInstant(mid, timezoneId);
        if (offMid === offBefore) {
          low = mid;
        } else {
          high = mid;
        }
      }

      const transitionUtcMs = high;
      const gapStartLocalMs = transitionUtcMs + (offBefore * 1000);
      const gapEndLocalMs = transitionUtcMs + (offAfter * 1000);

      if (targetLocalAsUtcMs >= gapStartLocalMs && targetLocalAsUtcMs < gapEndLocalMs) {
        const gapMinutes = Math.round((offAfter - offBefore) / 60);
        return {
          transitionUtcMs,
          offsetBefore: offBefore,
          offsetAfter: offAfter,
          gapMinutes
        };
      }
    }
  }

  return null;
}

/**
 * Classifies the seasonal / historical status of the offset.
 */
function classifyDstStatus(
  timezoneId: string,
  utcMs: number,
  offsetSeconds: number
): DstClassification {
  // Check India historical war time (1942-09-01 to 1945-10-15 UTC+6:30 = 23400s)
  if (timezoneId === 'Asia/Kolkata') {
    if (offsetSeconds === 23400) return 'HISTORICAL_WAR_TIME';
    if (offsetSeconds === 19270) return 'LOCAL_MEAN_TIME'; // Madras LMT pre-1906
    return 'STANDARD_TIME';
  }

  // General check comparing against mid-year and year-start offsets
  const year = new Date(utcMs).getUTCFullYear();
  const janMs = Date.UTC(year, 0, 15, 12, 0, 0);
  const julMs = Date.UTC(year, 6, 15, 12, 0, 0);

  const janOffset = getUtcOffsetSecondsForInstant(janMs, timezoneId);
  const julOffset = getUtcOffsetSecondsForInstant(julMs, timezoneId);

  const standardOffset = Math.min(janOffset, julOffset);
  if (janOffset !== julOffset && offsetSeconds > standardOffset) {
    return 'DAYLIGHT_SAVING_TIME';
  }
  return 'STANDARD_TIME';
}

// ============================================================================
// CORE RESOLUTION ALGORITHM
// ============================================================================

/**
 * Resolves a candidate local date and local time into normalized UTC instant(s)
 * using the host environment's native IANA timezone database.
 *
 * Algorithm:
 * 1. Validates calendar date, clock time, coordinates, and timezone identifier.
 * 2. Computes the target local timestamp as UTC epoch milliseconds (T_target).
 * 3. Scans nearby candidate offsets across historical transition horizons (±3 hours).
 * 4. Gathers all UTC instants whose local time decomposition matches T_target exactly.
 * 5. Categorizes outcome:
 *    - 1 match: RESOLVED_EXACT with verified round trip.
 *    - 2 matches: AMBIGUOUS_FALLBACK_DST (discloses both hypotheses; never silently chooses).
 *    - 0 matches: NONEXISTENT_SPRING_DST (discloses transition gap).
 */
export function resolveLocalToUtc(
  params: ResolveCandidateParams
): CandidateResolutionResult {
  const {
    localDate,
    localClockTime,
    timezoneId,
    latitude,
    longitude,
    placeName = 'Coordinates Station',
    sourceProvenance = 'CanonicalTimezoneResolver'
  } = params;

  // 1. Input Integrity Checks
  if (!isValidCalendarDate(localDate) || !isValidClockTime(localClockTime)) {
    return {
      status: 'INVALID_INPUT',
      errorMessage: `Malformed date ("${localDate}") or time ("${localClockTime}") string`,
      roundTripVerified: false
    };
  }

  if (typeof latitude !== 'number' || latitude < -90 || latitude > 90 ||
      typeof longitude !== 'number' || longitude < -180 || longitude > 180) {
    return {
      status: 'INVALID_INPUT',
      errorMessage: `Geographic coordinates out of bounds: lat ${latitude}, lon ${longitude}`,
      roundTripVerified: false
    };
  }

  if (!isValidIanaTimezone(timezoneId)) {
    return {
      status: 'UNRESOLVED_TIMEZONE',
      errorMessage: `Timezone identifier "${timezoneId}" is invalid or unrecognized by runtime`,
      roundTripVerified: false
    };
  }

  // 2. Parse decomposed local parameters
  const [y, m, d] = localDate.split('-').map(Number);
  const [h, min, s] = localClockTime.split(':').map(Number);
  const targetLocalAsUtcMs = Date.UTC(y, m - 1, d, h, min, s);

  // 3. Estimate UTC instant and evaluate transition candidates
  const estimatedUtcMs = targetLocalAsUtcMs;
  const initialOffsetSeconds = getUtcOffsetSecondsForInstant(estimatedUtcMs, timezoneId);
  const primaryUtcMs = targetLocalAsUtcMs - (initialOffsetSeconds * 1000);

  const matchedUtcInstants: number[] = [];
  const checkedUtcInstants = new Set<number>();
  const discoveredOffsets = new Set<number>([initialOffsetSeconds]);

  const testCandidate = (testUtcMs: number) => {
    if (checkedUtcInstants.has(testUtcMs)) return;
    checkedUtcInstants.add(testUtcMs);

    const { localAsUtcMs } = formatUtcToLocal(testUtcMs, timezoneId);
    if (localAsUtcMs === targetLocalAsUtcMs) {
      matchedUtcInstants.push(testUtcMs);
    }
  };

  // Sample offsets across standard DST horizons (±4 hours) and extended civil transitions
  // (including large date-line and historical shifts up to ±26 hours, such as Pacific/Apia 2011)
  const hourOffsetsToTest = [
    0, -1, 1, -2, 2, -3, 3, -4, 4,
    -12, 12, -13, 13, -24, 24, -25, 25, -26, 26
  ];

  for (const hOffset of hourOffsetsToTest) {
    const probeUtcMs = primaryUtcMs + (hOffset * 3600 * 1000);
    const probeOffsetSeconds = getUtcOffsetSecondsForInstant(probeUtcMs, timezoneId);
    discoveredOffsets.add(probeOffsetSeconds);
    const candidateUtcMs = targetLocalAsUtcMs - (probeOffsetSeconds * 1000);
    testCandidate(candidateUtcMs);
  }

  // Iterative discovery: check offsets present at the generated candidate instants
  for (const candidateUtc of Array.from(checkedUtcInstants)) {
    const candOffset = getUtcOffsetSecondsForInstant(candidateUtc, timezoneId);
    if (!discoveredOffsets.has(candOffset)) {
      discoveredOffsets.add(candOffset);
      const newCandidateUtc = targetLocalAsUtcMs - (candOffset * 1000);
      testCandidate(newCandidateUtc);
    }
  }

  // Sort ascending by time
  matchedUtcInstants.sort((a, b) => a - b);

  // 4. Case: Exactly One Match (RESOLVED_EXACT)
  if (matchedUtcInstants.length === 1) {
    const utcMs = matchedUtcInstants[0];
    const offsetSeconds = getUtcOffsetSecondsForInstant(utcMs, timezoneId);
    const utcInstant = new Date(utcMs).toISOString();

    // Verify character-for-character round trip
    const roundTrip = formatUtcToLocal(utcMs, timezoneId);
    const roundTripVerified =
      roundTrip.localDate === localDate && roundTrip.localTime === localClockTime;

    if (!roundTripVerified) {
      return {
        status: 'UNRESOLVED_TIMEZONE',
        errorMessage: `Round-trip verification mismatch: expected ${localDate} ${localClockTime}, got ${roundTrip.localDate} ${roundTrip.localTime}`,
        roundTripVerified: false
      };
    }

    const candidate: CandidateTuple = {
      localDate,
      localClockTime,
      latitude,
      longitude,
      placeName,
      timezoneId,
      utcOffsetSeconds: offsetSeconds,
      utcInstant,
      sourceProvenance,
      calculationVersion: TIMEZONE_ENGINE_VERSION
    };

    const tupleValidation = validateCandidateTuple(candidate);
    if (!tupleValidation.isValid) {
      return {
        status: 'INVALID_INPUT',
        errorMessage: `Candidate tuple contract violation: ${tupleValidation.errors.join(', ')}`,
        roundTripVerified: false
      };
    }

    return {
      status: 'RESOLVED_EXACT',
      resolvedCandidate: candidate,
      roundTripVerified: true
    };
  }

  // 5. Case: Exactly Two Matches (AMBIGUOUS_FALLBACK_DST)
  if (matchedUtcInstants.length === 2) {
    const [utcMs1, utcMs2] = matchedUtcInstants;
    const offSec1 = getUtcOffsetSecondsForInstant(utcMs1, timezoneId);
    const offSec2 = getUtcOffsetSecondsForInstant(utcMs2, timezoneId);

    const hyp1: UtcHypothesis = {
      utcInstant: new Date(utcMs1).toISOString(),
      utcMs: utcMs1,
      utcOffsetSeconds: offSec1,
      formattedOffset: formatOffsetSeconds(offSec1),
      dstStatus: classifyDstStatus(timezoneId, utcMs1, offSec1)
    };

    const hyp2: UtcHypothesis = {
      utcInstant: new Date(utcMs2).toISOString(),
      utcMs: utcMs2,
      utcOffsetSeconds: offSec2,
      formattedOffset: formatOffsetSeconds(offSec2),
      dstStatus: classifyDstStatus(timezoneId, utcMs2, offSec2)
    };

    return {
      status: 'AMBIGUOUS_FALLBACK_DST',
      ambiguousHypotheses: [hyp1, hyp2],
      errorMessage: `Ambiguous local clock time: ${localDate} ${localClockTime} occurs twice during backward DST transition. Two valid UTC hypotheses exist (${hyp1.formattedOffset} vs ${hyp2.formattedOffset}).`,
      roundTripVerified: true
    };
  }

  // 6. Case: Zero Matches (NONEXISTENT_SPRING_DST)
  if (matchedUtcInstants.length === 0) {
    // Derive verified transition boundary from offset discontinuity bisection
    const boundary = findTransitionBoundary(
      targetLocalAsUtcMs,
      timezoneId,
      Array.from(discoveredOffsets)
    );

    if (boundary) {
      const boundaryIso = new Date(boundary.transitionUtcMs).toISOString();
      return {
        status: 'NONEXISTENT_SPRING_DST',
        gapBoundaries: {
          preTransitionUtc: boundaryIso,
          postTransitionUtc: boundaryIso,
          gapMinutes: boundary.gapMinutes,
          transitionEpochMs: boundary.transitionUtcMs
        },
        errorMessage: `Nonexistent local clock time: ${localDate} ${localClockTime} fell within a forward transition gap of ${boundary.gapMinutes} minutes (transition instant ${boundaryIso}) and was skipped by the local civil clock.`,
        roundTripVerified: false
      };
    }

    // Explicit unresolved diagnostic when exact transition boundary cannot be verified
    return {
      status: 'NONEXISTENT_SPRING_DST',
      errorMessage: `Nonexistent local clock time: ${localDate} ${localClockTime} produced zero matching UTC instants during a civil transition. Exact transition boundary could not be verified.`,
      roundTripVerified: false
    };
  }

  // 7. Fallback for unprecedented multi-match anomalies
  return {
    status: 'UNRESOLVED_TIMEZONE',
    errorMessage: `Unprecedented timezone anomaly: local time produced ${matchedUtcInstants.length} UTC matches`,
    roundTripVerified: false
  };
}

